import { localize, pickLocalizedField } from 'mixins/helpers';

export const ADS = [
    {
        value: 'INITIATION_BANKRUPTCY_ANNOUNCEMENT',
        label: 'find-ads-full-announcement-bankruptcy',
    },
    {
        value: 'INITIATION_REHABILITATION_ANNOUNCEMENT',
        label: 'find-ads-full-announcement-proceedings',
    },

    {
        value: 'DECLARING_BANKRUPT_ANNOUNCEMENT',
        label: 'find-ads-full-announcement-recognition',
    },
    {
        value: 'BANKRUPTCY_MEETING_NOTICE',
        label: 'find-ads-full-notification-bankruptcy',
    },
    {
        value: 'REHABILITATION_MEETING_NOTICE',
        label: 'find-ads-full-notification-rehabilitation',
    },
    {
        value: 'LIQUIDATION_BANKRUPT_ANNOUNCEMENT',
        label: 'find-ads-full-announcement-liquidation',
    },
    {
        value: 'COLLATERAL_ASSESSMENT_ANNOUNCEMENT',
        label: 'find-ads-full-announcement-collateral',
    },
];

const ANNOUNCEMENT_TYPE_ALIASES: Record<string, string> = {
    COLLATERAL_VALUATION_ANNOUNCEMENT: 'COLLATERAL_ASSESSMENT_ANNOUNCEMENT',
};

export const normalizeAnnouncementType = (type?: string) =>
    (type && ANNOUNCEMENT_TYPE_ALIASES[type]) || type || '';

const formatPortalDate = (value?: string | null) => {
    if (!value) return '';

    const head = value.substring(0, 10);

    if (/^\d{4}-\d{2}-\d{2}$/.test(head)) {
        return head.split('-').reverse().join('.');
    }

    if (/^\d{2}\.\d{2}\.\d{4}$/.test(head)) {
        return head;
    }

    return value;
};

const formatPortalTime = (value?: string | null) => {
    if (!value) return '';

    const match = value.match(/(\d{2}:\d{2})/);
    return match ? match[1] : value;
};

const personName = (person: any) => {
    if (!person) return '';
    if (typeof person === 'string') return person;

    return (
        person.fullName ||
        [person.lastName, person.firstName, person.middleName].filter(Boolean).join(' ')
    );
};

const propertyValues = (row: any, fields: string[]) => {
    const list = Array.isArray(row?.properties)
        ? row.properties
        : Array.isArray(row?.propertyList)
          ? row.propertyList
          : null;

    if (list) {
        return list
            .map((item: any) =>
                fields.map((field) => item?.[field]).find((value) => value != null && value !== '')
            )
            .filter((value) => value != null && value !== '')
            .join('; ');
    }

    const value = fields.map((field) => row?.[field]).find((item) => item != null && item !== '');
    return value ?? '';
};

const localizedOrPlain = (row: any, lang: string, field: string) =>
    pickLocalizedField(row, lang, [field]) || row?.[field] || '';

export const INITIATION_BANKRUPTCY_ANNOUNCEMENT = (translations: any, lang: string) => {
    return [
        {
            title: localize('region', translations),
            render: (row: any) => pickLocalizedField(row, lang, ['region']),
        },
        {
            title: localize('find-ads-full-name', translations),
            dataIndex: 'debtor',
            render: (debtor: any) => pickLocalizedField(debtor, lang),
        },
        {
            title: localize('find-ads-full-debtor-iin', translations),
            dataIndex: ['debtor', 'taxpayerCode'],
        },
        {
            title: localize('find-ads-full-debtor-location', translations),
            dataIndex: ['debtor'],
            render: (debtor: any) => pickLocalizedField(debtor, lang, ['address']),
        },
        {
            title: localize('find-ads-full-bankruptcy-proceedings', translations),
            dataIndex: ['court'],
            render: (court: any) => pickLocalizedField(court, lang, ['name']),
        },
        {
            title: localize('find-ads-full-date-ruling', translations),
            dataIndex: 'court',
            render: (court: any) =>
                court.courtDate && court.courtDate.substring(0, 10).split('-').reverse().join('.'),
        },
        {
            title: localize('find-ads-full-surname-temporary-manager', translations),
            dataIndex: 'admin',
            render: (admin) =>
                (admin.lastName ?? '') +
                ' ' +
                (admin.firstName ?? '') +
                ' ' +
                (admin.middleName ?? ''),
        },
        {
            title: localize('find-ads-full-deadline-temporary-manager', translations),
            children: [
                {
                    title: localize('find-ads-from', translations),
                    dataIndex: 'beginDate',
                    width: '5%',
                    render: (beginDate) =>
                        beginDate && beginDate.substring(0, 10).split('-').reverse().join('.'),
                },
                {
                    title: localize('find-ads-to', translations),
                    dataIndex: 'endDate',
                    width: '5%',
                    render: (endDate) =>
                        endDate && endDate.substring(0, 10).split('-').reverse().join('.'),
                },
            ],
        },
        {
            title: localize('find-ads-full-address-requirements', translations),
            render: (row: any) => pickLocalizedField(row, lang, ['rtkAddress']),
        },
        {
            title: localize('find-ads-full-contact-details', translations),
            dataIndex: 'admin',
            render: (admin) => admin.phone + ' ' + admin.email,
        },
        {
            title: localize('find-ads-full-placement-date', translations),
            dataIndex: 'createdDate',
            render: (createdDate) =>
                createdDate && createdDate.substring(0, 10).split('-').reverse().join('.'),
        },
    ];
};

export const DECLARING_BANKRUPT_ANNOUNCEMENT = (translations: any, lang: string) => {
    return [
        {
            title: localize('region', translations),
            render: (row: any) => pickLocalizedField(row, lang, ['region']),
        },
        {
            title: localize('find-ads-full-name', translations),
            dataIndex: 'debtor',
            render: (debtor: any) =>
                pickLocalizedField(debtor, lang) ||
                [debtor.lastName, debtor.firstName, debtor.middleName].filter(Boolean).join(' '),
        },
        {
            title: localize('find-ads-full-debtor-iin', translations),
            dataIndex: ['debtor', 'taxpayerCode'],
        },
        {
            title: localize('find-ads-full-debtor-location', translations),
            dataIndex: ['debtor'],
            render: (debtor: any) => pickLocalizedField(debtor, lang, ['address']),
        },
        {
            title: localize('find-ads-full-information-state-registration', translations),
            dataIndex: 'regInfo',
        },
        {
            title: localize('find-ads-full-name-decision-declaring', translations),
            dataIndex: ['court'],
            render: (court: any) => pickLocalizedField(court, lang, ['name']),
        },
        {
            title: localize('find-ads-full-date-decision', translations),
            dataIndex: 'court',
            render: (court) =>
                court.courtDate && court.courtDate.substring(0, 10).split('-').reverse().join('.'),
        },
        {
            title: localize('find-ads-full-placement-date', translations),
            dataIndex: 'createdDate',
            render: (createdDate) =>
                createdDate && createdDate.substring(0, 10).split('-').reverse().join('.'),
        },
    ];
};

export const BANKRUPTCY_MEETING_NOTICE = (translations: any, lang: string = 'ru') => {
    return [
        {
            title: localize('region', translations),
            render: (row: any) => pickLocalizedField(row, lang, ['region']),
        },
        {
            title: localize('find-ads-full-name', translations),
            dataIndex: 'debtor',
            render: (debtor: any) =>
                pickLocalizedField(debtor, lang, ['']) ||
                [debtor.lastName, debtor.firstName, debtor.middleName].filter(Boolean).join(' '),
        },
        {
            title: localize('find-ads-full-debtor-iin', translations),
            dataIndex: ['debtor', 'taxpayerCode'],
        },
        {
            title: localize('find-ads-full-debtor-location', translations),
            render: (row: any) => pickLocalizedField(row.debtor, lang, ['address']),
        },
        {
            title: localize('find-ads-full-date-meeting', translations),
            dataIndex: 'meetingDate',
            render: (meetingDate) =>
                meetingDate && meetingDate.substring(0, 10).split('-').reverse().join('.'),
        },
        {
            title: localize('find-ads-full-time-meeting', translations),
            dataIndex: 'meetingDate',
            render: (meetingDate) => meetingDate && meetingDate.substring(11, 16),
        },
        {
            title: localize('find-ads-full-venue-meeting', translations),
            render: (row: any) => pickLocalizedField(row, lang, ['meetingPlace']),
        },
        {
            title: localize('find-ads-full-agenda-meeting', translations),
            dataIndex: 'meetingAgenda',
        },
        {
            title: localize('find-ads-full-procedure', translations),
            dataIndex: 'meetingFamiliarization',
        },
        {
            title: localize('find-ads-full-contact-details', translations),
            dataIndex: 'admin',
            render: (admin) => admin.phone + ' ' + admin.email,
        },
        {
            title: localize('find-ads-full-placement-date', translations),
            dataIndex: 'createdDate',
            render: (createdDate) =>
                createdDate && createdDate.substring(0, 10).split('-').reverse().join('.'),
        },
    ];
};

export const LIQUIDATION_BANKRUPT_ANNOUNCEMENT = (translations: any, lang: string) => {
    return [
        {
            title: localize('region', translations),
            dataIndex: 'regionRu',
            key: 'regionRu',
        },
        {
            title: localize('find-ads-full-name', translations),
            dataIndex: 'debtor',
            render: (debtor: any) =>
                pickLocalizedField(debtor, lang) ||
                [debtor.lastName, debtor.firstName, debtor.middleName].filter(Boolean).join(' '),
        },
        {
            title: localize('find-ads-full-debtor-iin', translations),
            dataIndex: ['debtor', 'taxpayerCode'],
        },
        {
            title: localize('find-ads-full-debtor-location', translations),
            dataIndex: ['debtor'],
            render: (debtor: any) => pickLocalizedField(debtor, lang, ['address']),
        },
        {
            title: localize('find-ads-full-court-name-liquidation', translations),
            dataIndex: ['court'],
            render: (court: any) => pickLocalizedField(court, lang, ['name']),
        },
        {
            title: localize('find-ads-full-employee', translations),
            dataIndex: 'employee',
        },
        {
            title: localize('find-ads-full-act-issue-date', translations),
            dataIndex: 'actIssueDate',
            render: (actIssueDate) =>
                actIssueDate && actIssueDate.substring(0, 10).split('-').reverse().join('.'),
        },
        {
            title: localize('find-ads-full-act-issue-number', translations),
            dataIndex: 'actIssueNumber',
        },
        {
            title: localize('find-ads-full-act-decision-date', translations),
            dataIndex: 'actDecisionDate',
            render: (actDecisionDate) =>
                actDecisionDate && actDecisionDate.substring(0, 10).split('-').reverse().join('.'),
        },
        {
            title: localize('find-ads-full-placement-date', translations),
            dataIndex: 'createdDate',
            render: (createdDate) =>
                createdDate && createdDate.substring(0, 10).split('-').reverse().join('.'),
        },
    ];
};

export const COLLATERAL_ASSESSMENT_ANNOUNCEMENT = (translations: any, lang: string) => {
    return [
        {
            title: localize('region', translations),
            render: (row: any) => pickLocalizedField(row, lang, ['region']) || row?.regionRu || '',
        },
        {
            title: localize('find-ads-full-name', translations),
            dataIndex: 'debtor',
            render: (debtor: any) =>
                pickLocalizedField(debtor, lang) ||
                [debtor?.lastName, debtor?.firstName, debtor?.middleName].filter(Boolean).join(' '),
        },
        {
            title: localize('find-ads-full-debtor-iin', translations),
            dataIndex: ['debtor', 'taxpayerCode'],
        },
        {
            title: localize('find-ads-full-debtor-location', translations),
            dataIndex: ['debtor'],
            render: (debtor: any) => pickLocalizedField(debtor, lang, ['address']),
        },
        {
            title: localize('find-ads-collateral-debtor-phone', translations),
            render: (row: any) => row?.debtorPhoneNumber || row?.debtor?.phone || '',
        },
        {
            title: localize('find-ads-collateral-manager-iin', translations),
            render: (row: any) =>
                row?.admin?.identifier ||
                row?.admin?.taxpayerCode ||
                row?.adminData?.identifier ||
                '',
        },
        {
            title: localize('find-ads-collateral-manager-name', translations),
            render: (row: any) => personName(row?.admin) || personName(row?.adminData),
        },
        {
            title: localize('find-ads-collateral-department', translations),
            render: (row: any) =>
                localizedOrPlain(row, lang, 'departmentName') ||
                row?.admin?.departmentName ||
                row?.adminData?.departmentName ||
                '',
        },
        {
            title: localize('find-ads-collateral-order-number', translations),
            render: (row: any) =>
                row?.orderNumber || row?.admin?.orderNumber || row?.adminData?.orderNumber || '',
        },
        {
            title: localize('find-ads-collateral-order-date', translations),
            render: (row: any) =>
                formatPortalDate(
                    row?.orderDate || row?.admin?.orderDate || row?.adminData?.orderDate
                ),
        },
        {
            title: localize('find-ads-collateral-announcement-date', translations),
            dataIndex: 'documentDate',
            render: (documentDate: string) => formatPortalDate(documentDate),
        },
        {
            title: localize('find-ads-collateral-object-name', translations),
            render: (row: any) => propertyValues(row, ['objectName']),
        },
        {
            title: localize('find-ads-collateral-cadastral-number', translations),
            render: (row: any) => propertyValues(row, ['cadastralNumber']),
        },
        {
            title: localize('find-ads-collateral-object-address', translations),
            render: (row: any) => propertyValues(row, ['objectAddress']),
        },
        {
            title: localize('find-ads-collateral-construction-year', translations),
            render: (row: any) => propertyValues(row, ['builtYear', 'constructionYear']),
        },
        {
            title: localize('find-ads-collateral-technical-condition', translations),
            render: (row: any) => propertyValues(row, ['technicalCondition']),
        },
        {
            title: localize('find-ads-collateral-note', translations),
            render: (row: any) => propertyValues(row, ['otherInfo', 'note']),
        },
        {
            title: localize('find-ads-collateral-application-period', translations),
            children: [
                {
                    title: localize('find-ads-from', translations),
                    width: '5%',
                    render: (row: any) => formatPortalTime(row?.applicationFrom),
                },
                {
                    title: localize('find-ads-to', translations),
                    width: '5%',
                    render: (row: any) =>
                        formatPortalTime(row?.applicationBy || row?.applicationTo),
                },
            ],
        },
        {
            title: localize('find-ads-collateral-application-break', translations),
            children: [
                {
                    title: localize('find-ads-from', translations),
                    width: '5%',
                    render: (row: any) => formatPortalTime(row?.applicationBreakFrom),
                },
                {
                    title: localize('find-ads-to', translations),
                    width: '5%',
                    render: (row: any) =>
                        formatPortalTime(row?.applicationBreakBy || row?.applicationBreakTo),
                },
            ],
        },
        {
            title: localize('find-ads-collateral-application-address', translations),
            render: (row: any) => localizedOrPlain(row, lang, 'applicationAddress'),
        },
        {
            title: localize('find-ads-collateral-application-phone', translations),
            render: (row: any) => row?.applicationPhoneNumber || row?.applicationPhone || '',
        },
        {
            title: localize('find-ads-collateral-claims-period', translations),
            children: [
                {
                    title: localize('find-ads-from', translations),
                    width: '5%',
                    render: (row: any) => formatPortalTime(row?.claimsFrom),
                },
                {
                    title: localize('find-ads-to', translations),
                    width: '5%',
                    render: (row: any) => formatPortalTime(row?.claimsBy || row?.claimsTo),
                },
            ],
        },
        {
            title: localize('find-ads-collateral-claims-break', translations),
            children: [
                {
                    title: localize('find-ads-from', translations),
                    width: '5%',
                    render: (row: any) => formatPortalTime(row?.claimsBreakFrom),
                },
                {
                    title: localize('find-ads-to', translations),
                    width: '5%',
                    render: (row: any) =>
                        formatPortalTime(row?.claimsBreakBy || row?.claimsBreakTo),
                },
            ],
        },
        {
            title: localize('find-ads-collateral-claims-address', translations),
            render: (row: any) => localizedOrPlain(row, lang, 'claimsAddress'),
        },
        {
            title: localize('find-ads-collateral-claims-phone', translations),
            render: (row: any) => row?.claimsPhoneNumber || row?.claimsPhone || '',
        },
        {
            title: localize('find-ads-collateral-claims-email', translations),
            dataIndex: 'claimsEmail',
        },
        {
            title: localize('find-ads-full-placement-date', translations),
            dataIndex: 'createdDate',
            render: (createdDate: string) => formatPortalDate(createdDate),
        },
    ];
};

export const getColumnsByType = (
    type: string,
    translations: Record<string, string>,
    lang: string = 'ru'
) => {
    switch (normalizeAnnouncementType(type)) {
        case 'INITIATION_BANKRUPTCY_ANNOUNCEMENT':
            return INITIATION_BANKRUPTCY_ANNOUNCEMENT(translations, lang);
        case 'INITIATION_REHABILITATION_ANNOUNCEMENT':
            return INITIATION_BANKRUPTCY_ANNOUNCEMENT(translations, lang);
        case 'DECLARING_BANKRUPT_ANNOUNCEMENT':
            return DECLARING_BANKRUPT_ANNOUNCEMENT(translations, lang);
        case 'BANKRUPTCY_MEETING_NOTICE':
            return BANKRUPTCY_MEETING_NOTICE(translations, lang);
        case 'REHABILITATION_MEETING_NOTICE':
            return BANKRUPTCY_MEETING_NOTICE(translations, lang);
        case 'LIQUIDATION_BANKRUPT_ANNOUNCEMENT':
            return LIQUIDATION_BANKRUPT_ANNOUNCEMENT(translations, lang);
        case 'COLLATERAL_ASSESSMENT_ANNOUNCEMENT':
            return COLLATERAL_ASSESSMENT_ANNOUNCEMENT(translations, lang);
        default:
            return [];
    }
};
